const EVENT_FIELDS = Object.freeze({
  'search.results_viewed': ['result_count', 'product_count'],
  'search.result_opened': ['product_id'],
  'size_guide.opened': ['product_id'],
  'size_guide.unit_changed': ['product_id', 'unit'],
  'try_on.opened': ['product_id', 'variant_id'],
  'try_on.photo_selected': ['product_id', 'variant_id'],
  'try_on.generate_started': ['product_id', 'variant_id'],
  'try_on.generate_succeeded': ['product_id', 'variant_id'],
  'try_on.generate_failed': ['product_id', 'variant_id', 'reason'],
  'back_in_stock.opened': ['product_id', 'variant_id'],
  'back_in_stock.submitted': ['product_id', 'variant_id'],
  'back_in_stock.succeeded': ['product_id', 'variant_id'],
  'back_in_stock.failed': ['product_id', 'variant_id', 'reason'],
  'lookbook.opened': ['item_count'],
  'lookbook.product_opened': ['product_id'],
  'cart.item_added': ['product_id', 'variant_id', 'quantity'],
  'cart.updated': ['item_count'],
  'cart.error': ['reason'],
  'quick_cart.opened': ['item_count'],
});

const COUNT_FIELDS = new Set(['result_count', 'product_count', 'item_count', 'quantity']);
const ID_FIELDS = new Set(['product_id', 'variant_id']);
const REASONS = new Set(['endpoint', 'file_size', 'limit', 'network', 'response', 'unknown', 'validation']);

const sanitizeValue = (key, value) => {
  if (COUNT_FIELDS.has(key)) {
    const count = Number(value);
    return Number.isInteger(count) && count >= 0 && count <= 10000 ? count : undefined;
  }

  if (ID_FIELDS.has(key)) {
    const id = String(value ?? '');
    return /^[A-Za-z0-9:_-]{1,64}$/.test(id) ? id : undefined;
  }

  if (key === 'unit') {
    return value === 'cm' || value === 'in' ? value : undefined;
  }

  if (key === 'reason') {
    return REASONS.has(value) ? value : undefined;
  }

  return undefined;
};

export const sanitizeEvidence = (name, payload = {}) => {
  const fields = EVENT_FIELDS[name];
  if (!fields) return null;

  const input = payload && typeof payload === 'object' ? payload : {};
  const sanitizedPayload = {};

  fields.forEach((field) => {
    const value = sanitizeValue(field, input[field]);
    if (value !== undefined) sanitizedPayload[field] = value;
  });

  return { name, payload: sanitizedPayload };
};

export const trackEvidence = (name, payload = {}, target = globalThis.document) => {
  const evidence = sanitizeEvidence(name, payload);
  if (!evidence) return null;

  try {
    if (typeof globalThis.clarity === 'function') globalThis.clarity('event', evidence.name);
  } catch {}

  if (target && typeof globalThis.CustomEvent === 'function') {
    target.dispatchEvent(new CustomEvent('budziko:evidence:recorded', { detail: evidence }));
  }

  return evidence;
};

const extractCartCount = (cart) => cart?.item_count ?? cart?.itemCount;

const contextFromElement = (element) => {
  const root = element?.closest?.('[data-product-id]') || document.querySelector('[data-virtual-try-on]');
  return {
    product_id: root?.dataset?.productId,
    variant_id: root?.dataset?.variantId || root?.dataset?.initialVariantId,
  };
};

const initializeBrowserEvidence = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (window.BudzikoEvidence?.initialized) return;

  window.BudzikoEvidence = Object.freeze({ initialized: true, track: trackEvidence });

  document.addEventListener('click', (event) => {
    const target = event.target.closest?.('a, button');
    if (!target) return;

    if (target.matches('[data-open-try-on]')) {
      trackEvidence('try_on.opened', contextFromElement(target));
    } else if (target.matches('[data-back-in-stock-trigger]')) {
      trackEvidence('back_in_stock.opened', contextFromElement(target));
    } else if (target.matches('[data-lookbook-open]')) {
      trackEvidence('lookbook.opened', { item_count: target.dataset.itemCount });
    } else if (target.matches('.variant-popup__trigger')) {
      trackEvidence('size_guide.opened', contextFromElement(target));
    } else if (target.closest('[data-lookbook-product-id]')) {
      trackEvidence('lookbook.product_opened', {
        product_id: target.closest('[data-lookbook-product-id]').dataset.lookbookProductId,
      });
    } else if (target.closest('[data-search-product-id]')) {
      trackEvidence('search.result_opened', {
        product_id: target.closest('[data-search-product-id]').dataset.searchProductId,
      });
    }
  });

  document.addEventListener('flu:cart:item-added', (event) => {
    const product = event.detail?.product || {};
    trackEvidence('cart.item_added', {
      product_id: product.product_id || product.productId || product.id,
      variant_id: product.variant_id || product.variantId,
      quantity: product.quantity || 1,
    });
  });

  document.addEventListener('flu:cart:updated', (event) => {
    trackEvidence('cart.updated', { item_count: extractCartCount(event.detail?.cart) });
  });

  document.addEventListener('flu:cart:error', () => {
    trackEvidence('cart.error', { reason: 'validation' });
  });

  document.addEventListener('flu:quick-cart:open', (event) => {
    trackEvidence('quick_cart.opened', { item_count: extractCartCount(event.detail?.cart) });
  });

  document.querySelectorAll('[data-search-item-count]:not([data-evidence-ready])').forEach((root) => {
    root.dataset.evidenceReady = 'true';
    trackEvidence('search.results_viewed', {
      result_count: root.dataset.searchItemCount,
      product_count: root.dataset.searchProductCount,
    });
  });
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBrowserEvidence, { once: true });
  } else {
    initializeBrowserEvidence();
  }

  document.addEventListener('shopify:section:load', initializeBrowserEvidence);
}
